import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatTimeOnly as formatTime } from '../utils/helpers';
import { useChat } from '../context/ChatContext';
import Sidebar from '../components/Chat/Sidebar';
import ChatWindow from '../components/Chat/ChatWindow';
import ProfilePanel from '../components/Chat/ProfilePanel';
import StatusTab from '../components/Chat/StatusTab';
import CallsTab from '../components/Chat/CallsTab';
import BillsTab from '../components/Chat/BillsTab';
import WeighmentTab from '../components/Chat/WeighmentTab';
import NearbyBusinessTab from '../components/Chat/NearbyBusinessTab';
import CallScreen from '../components/Chat/CallScreen';
import { NewChatModal, NewGroupModal, MyProfileModal, SettingsModal } from '../components/Modals/Modals';
import { callsAPI } from '../utils/api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function ChatPage() {
  const { tab, setTab, activeConvId, setActiveConvId, incomingCall, setIncomingCall } = useChat();
  const isMobile = useIsMobile();

  const [profileConv, setProfileConv] = useState(null);
  const [modal, setModal] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [mobilePanel, setMobilePanel] = useState('list');
  const [toasts, setToasts] = useState([]);
  const callPollRef = useRef(null);

  const addGlobalToast = useCallback((msg, type = 'info', title = 'Notification') => {
    const id = Date.now();
    const time = formatTime(new Date());
    setToasts(t => [...t, { id, msg, type, time, title }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 5000);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const notification = e.detail || {};
      console.log('[Notification Debug] Incoming detail:', notification);

      // 1. Priority check for displayable text
      // We prefer 'message' as it's typically pre-processed/sanitized by the dispatcher.
      // We fallback to 'data' or 'content' for broader compatibility.
      let message = notification.message || notification.data || notification.content || 'New notification';

      // 2. Safety: If the message is a literal string "undefined", "[object Object]", or empty, use a fallback
      if (!message || message === 'undefined' || message === '[object Object]') {
        message = 'New weighment received';
      }

      // 3. String-level hardening: Never show the word "undefined" in the UI
      if (typeof message === 'string') {
        // Handle both lowercase and capitalized "undefined"
        message = message.replace(/undefined/gi, 'N/A');
      } else {
        // If it's not a string at this point, force a fallback
        message = 'New weighment received';
      }

      addGlobalToast(message, notification.type || 'info', notification.title || 'Notification');
    };
    window.addEventListener('app:notification', handler);
    return () => window.removeEventListener('app:notification', handler);
  }, [addGlobalToast]);

  // Removed: Call polling moved to ChatContext.jsx

  useEffect(() => {
    if (activeConvId && isMobile) setMobilePanel('chat');
  }, [activeConvId, isMobile]);

  useEffect(() => {
    if (isMobile && (tab === 'status' || tab === 'calls' || tab === 'bills' || tab === 'weighments' || tab === 'nearby')) {
      setMobilePanel('chat');
    }
  }, [tab, isMobile]);

  const handleBack = useCallback(() => {
    setMobilePanel('list');
    if (tab !== 'chats') { setTab('chats'); } else { setActiveConvId(null); }
    setProfileConv(null);
  }, [tab, setTab, setActiveConvId]);

  const handleStartCall = (conv, callType) => { setIncomingCall(null); setActiveCall({ conv, callType, isIncoming: false, incomingCall: null }); };
  const handleEndCall = () => { setActiveCall(null); setIncomingCall(null); };
  const handleOpenProfile = (conv) => setProfileConv(conv);

  const handleAnswerIncoming = () => {
    if (!incomingCall) return;
    setActiveCall({
      conv: { id: incomingCall.conversation_id, name: incomingCall.caller_name || incomingCall.conv_name, other_user: { name: incomingCall.caller_name, avatar: incomingCall.caller_avatar } },
      callType: incomingCall.type,
      isIncoming: true,
      incomingCall,
    });
    setIncomingCall(null);
  };

  const TabHeader = ({ title, onBack }) => (
    <div style={{ background: 'var(--header-bg)', padding: isMobile ? '10px 12px' : '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, minHeight: 56 }}>
      {onBack && <button className="icon-btn dark" onClick={onBack} style={{ color: 'white' }}>◀</button>}
      <h2 style={{ color: 'white', fontSize: 17, fontWeight: 700 }}>{title}</h2>
    </div>
  );

  // ── DESKTOP layout ──────────────────────────────────────────
  if (!isMobile) {
    return (
      <div className="app-shell">
        <Sidebar
          onNewChat={() => setModal('newchat')}
          onNewGroup={() => setModal('newgroup')}
          onProfile={() => setModal('profile')}
          onSettings={() => setModal('settings')}
        />

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minWidth: 0 }}>
          {tab === 'chats' && (
            <>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                <ChatWindow onOpenProfile={handleOpenProfile} onStartCall={handleStartCall} />
              </div>
              {profileConv && <ProfilePanel conv={profileConv} onClose={() => setProfileConv(null)} />}
            </>
          )}
          {tab === 'status' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }}>
              <TabHeader title="Status" />
              <StatusTab />
            </div>
          )}
          {tab === 'calls' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }}>
              <TabHeader title="Calls" />
              <CallsTab onStartCall={handleStartCall} />
            </div>
          )}
          {tab === 'bills' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }}>
              <TabHeader title="Bills" />
              <BillsTab />
            </div>
          )}
          {tab === 'weighments' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <TabHeader title="⚖️ Bills" />
              <WeighmentTab />
            </div>
          )}
          {tab === 'nearby' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <NearbyBusinessTab />
            </div>
          )}
        </div>

        {modal === 'newchat' && <NewChatModal onClose={() => setModal(null)} />}
        {modal === 'newgroup' && <NewGroupModal onClose={() => setModal(null)} />}
        {modal === 'profile' && <MyProfileModal onClose={() => setModal(null)} />}
        {modal === 'settings' && <SettingsModal onClose={() => setModal(null)} />}

        {incomingCall && !activeCall && (
          <IncomingCallToast
            call={incomingCall}
            onAnswer={handleAnswerIncoming}
            onDecline={async () => { await callsAPI.decline(incomingCall.id).catch(() => { }); setIncomingCall(null); }}
          />
        )}

        {activeCall && (
          <CallScreen
            conv={activeCall.conv}
            callType={activeCall.callType}
            isIncoming={activeCall.isIncoming || false}
            incomingCall={activeCall.incomingCall || null}
            onEnd={handleEndCall}
          />
        )}

        <ToastStack toasts={toasts} />
      </div>
    );
  }

  // ── MOBILE layout ───────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', background: 'var(--bg)' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', background: 'white',
        transform: mobilePanel === 'chat' ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 280ms cubic-bezier(.4,0,.2,1)',
        zIndex: 10, willChange: 'transform',
        pointerEvents: mobilePanel === 'list' ? 'auto' : 'none',
      }}>
        <Sidebar
          onNewChat={() => setModal('newchat')}
          onNewGroup={() => setModal('newgroup')}
          onProfile={() => setModal('profile')}
          onSettings={() => setModal('settings')}
        />
      </div>

      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', background: 'var(--chat-bg)',
        transform: mobilePanel === 'chat' ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 280ms cubic-bezier(.4,0,.2,1)',
        zIndex: 20, willChange: 'transform',
        pointerEvents: mobilePanel === 'chat' ? 'auto' : 'none',
      }}>
        {tab === 'chats' && (
          <>
            <ChatWindow onOpenProfile={handleOpenProfile} onStartCall={handleStartCall} onBack={handleBack} showBackButton />
            {profileConv && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 30, animation: 'slideInFromRight 250ms cubic-bezier(.4,0,.2,1)' }}>
                <ProfilePanel conv={profileConv} onClose={() => setProfileConv(null)} />
              </div>
            )}
          </>
        )}
        {tab === 'status' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }}>
            <TabHeader title="Status" onBack={handleBack} />
            <StatusTab />
          </div>
        )}
        {tab === 'calls' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }}>
            <TabHeader title="Recent Calls" onBack={handleBack} />
            <CallsTab onStartCall={handleStartCall} />
          </div>
        )}
        {tab === 'bills' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }}>
            <TabHeader title="Bills" onBack={handleBack} />
            <BillsTab />
          </div>
        )}
        {tab === 'weighments' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TabHeader title="⚖️ Bills" onBack={handleBack} />
            <WeighmentTab />
          </div>
        )}
        {tab === 'nearby' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TabHeader title="📍 Nearby" onBack={handleBack} />
            <NearbyBusinessTab />
          </div>
        )}
      </div>

      {modal === 'newchat' && <NewChatModal onClose={() => setModal(null)} />}
      {modal === 'newgroup' && <NewGroupModal onClose={() => setModal(null)} />}
      {modal === 'profile' && <MyProfileModal onClose={() => setModal(null)} />}
      {modal === 'settings' && <SettingsModal onClose={() => setModal(null)} />}

      {incomingCall && !activeCall && (
        <IncomingCallToast
          call={incomingCall}
          onAnswer={handleAnswerIncoming}
          onDecline={async () => { await callsAPI.decline(incomingCall.id).catch(() => { }); setIncomingCall(null); }}
        />
      )}

      {activeCall && (
        <CallScreen
          conv={activeCall.conv}
          callType={activeCall.callType}
          isIncoming={activeCall.isIncoming || false}
          incomingCall={activeCall.incomingCall || null}
          onEnd={handleEndCall}
        />
      )}
      <ToastStack toasts={toasts} />
    </div>
  );
}

function ToastStack({ toasts }) {
  return (
    <div style={{ position: 'fixed', bottom: 80, right: 12, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '12px 16px', borderRadius: 16, fontSize: 13, fontWeight: 600,
          background: t.type === 'success' ? '#064E3B' : t.type === 'error' ? '#7F1D1D' : '#1E3A5F',
          color: 'white', maxWidth: 300, boxShadow: '0 12px 30px rgba(0,0,0,.3)',
          animation: 'slideInFromBottom .4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          border: '1px solid rgba(255,255,255,0.1)',
          pointerEvents: 'auto',
          backdropFilter: 'blur(10px)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.8, letterSpacing: 0.6 }}>
              {t.title}
            </span>
            <span style={{ fontSize: 10, opacity: 0.6 }}>{t.time}</span>
          </div>
          <div style={{ lineHeight: 1.4 }}>{t.msg}</div>
        </div>
      ))}
    </div>
  );
}

function IncomingCallToast({ call, onAnswer, onDecline }) {
  return (
    <div style={{
      position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 900, width: 340, maxWidth: '95vw',
      background: 'white', borderRadius: 20,
      boxShadow: '0 12px 40px rgba(0,0,0,.25)',
      padding: '20px 24px',
      animation: 'bounceIn .3s cubic-bezier(.4,0,.2,1)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    }}>
      <div style={{ fontSize: 13, color: '#667781', fontWeight: 600, letterSpacing: '.3px', textTransform: 'uppercase' }}>
        Incoming {call.type} call
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#111B21' }}>{call.caller_name}</div>
      <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <button onClick={onDecline} style={{ width: 56, height: 56, borderRadius: '50%', background: '#EF4444', border: 'none', fontSize: 22, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📵</button>
          <span style={{ fontSize: 12, color: '#667781' }}>Decline</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <button onClick={onAnswer} style={{ width: 56, height: 56, borderRadius: '50%', background: '#10B981', border: 'none', fontSize: 22, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📞</button>
          <span style={{ fontSize: 12, color: '#667781' }}>Answer</span>
        </div>
      </div>
    </div>
  );
}
